import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Sidebar.css';
import clmLogo from '../../assets/clm-logo.svg';
import ProfileModal from '../profile/ProfileModal';
import ReactDOM from 'react-dom';
import { rolePermissions } from "../rbac/RolePermission";
import CircularProgress from '@mui/material/CircularProgress';
import PropTypes from 'prop-types';

// Helper: Collapsible Sidebar Section
const SidebarSection = ({ title, sectionKey, isOpen, toggleSection, children, titleTooltip }) => (
  <div className="sidebar-section">
    <div
      className="section-header no-underline"
      onClick={() => toggleSection(sectionKey)}
      onKeyDown={(e) => e.key === "Enter" && toggleSection(sectionKey)}
      tabIndex={0}
      role="button"
      title={titleTooltip}
    >
      <span className="chevron-icon">{isOpen ? '▼' : '▶'}</span>
      <span>{title}</span>
    </div>
    {isOpen && <div className="subsections">{children}</div>}
  </div>
);
SidebarSection.propTypes = {
  title: PropTypes.string.isRequired,
  sectionKey: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggleSection: PropTypes.func.isRequired,
  children: PropTypes.node,
  titleTooltip: PropTypes.string,
};
// Helper: Render Document Links
const renderDocumentsLinks = (getIsActive, setActiveSection, navigate, isClientPartner, permissions) => (
  <>
    <Link
      to="/clm_home"
      className={`sidebar-item no-underline ${getIsActive("/clm_home") ? "active" : ""}`}
      onClick={(e) => { setActiveSection('allContracts'); if (isClientPartner) { e.preventDefault(); navigate('/clm_home'); } }}
      title="View all your Signed contracts"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
        <polyline points="13 2 13 9 20 9" />
      </svg>
      <span>Signed Contracts</span>
    </Link>
    <Link
      to="/wip-contracts"
      className={`sidebar-item no-underline ${getIsActive("/wip-contracts") ? "active" : ""}`}
      onClick={(e) => { setActiveSection('wipContracts'); if (isClientPartner) { e.preventDefault(); navigate('/wip-contracts'); } }}
      title="View contracts currently in progress"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      <span>WIP Contracts</span>
    </Link>
    {/* {(permissions.sidebar.includes("IncompleteContracts")) && (
      <Link
        to="/incomplete-contracts"
        className={`sidebar-item no-underline ${getIsActive("/incomplete-contracts") ? "active" : ""}`}
        onClick={(e) => { setActiveSection('incompleteContracts'); if (isClientPartner) { e.preventDefault(); navigate('/incomplete-contracts'); } }}
        title="View contracts with missing required data"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <circle cx="12" cy="15" r="3" fill="currentColor"/>
          <path d="M12 12v6" stroke="white" strokeWidth="1"/>
        </svg>
        <span>Incomplete Contracts</span>
      </Link>
    )} */}
    {permissions.sidebar.includes("Templates") && (
      <Link
        to="/templates"
        className={`sidebar-item no-underline ${getIsActive("/templates") ? "active" : ""}`}
        title="Manage contract templates"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <line x1="9" y1="3" x2="9" y2="21" />
          <line x1="15" y1="3" x2="15" y2="21" />
        </svg>
        <span>Templates</span>
      </Link>
    )}
  </>
);

// Helper: Render My Collections Links
const renderCollectionsLinks = (getIsActive, permissions) => (
  <>
    <Link
      to="/favourites"
      className={`sidebar-item no-underline ${getIsActive("/favourites") ? "active" : ""}`}
      title="View your favorite contracts"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
      <span>Favourite</span>
    </Link>
    {permissions.sidebar.includes("Trash") && (
      <Link
        to="/trash"
        className={`sidebar-item no-underline ${getIsActive("/trash") ? "active" : ""}`}
        title="View archived contracts"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
        <span>Archive</span>
      </Link>
    )}
    {permissions.sidebar.includes("Recent") && (
      <Link
        to="/recents"
        className={`sidebar-item no-underline ${getIsActive("/recents") ? "active" : ""}`}
        title="View recently accessed contracts"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        <span>Recent</span>
      </Link>
    )}
  </>
);

// Helper: Render Master Links
const renderMasterLinks = (getIsActive, userRole, navigate) => (
  <>
    <Link
      to="/roles"
      className={`sidebar-item no-underline ${getIsActive("/roles") ? "active" : ""}`}
      title="Manage user roles and permissions"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
      <span>Roles</span>
    </Link>
    <Link
      to="/contract-status"
      className={`sidebar-item no-underline ${getIsActive("/contract-status") ? "active" : ""}`}
      title="Manage contract status types"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
      <span>Contract Status</span>
    </Link>
    <Link
      to="/contract-types"
      className={`sidebar-item no-underline ${getIsActive("/contract-types") ? "active" : ""}`}
      title="Manage contract types"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
      <span>Contract Types</span>
    </Link>
    <Link
      to="/practices"
      className={`sidebar-item no-underline ${getIsActive("/practices") ? "active" : ""}`}
      title="Manage practices"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      </svg>
      <span>Practices</span>
    </Link>
    <Link
      to="/currencies"
      className={`sidebar-item no-underline ${getIsActive("/currencies") ? "active" : ""}`}
      title="Manage currencies"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
      <span>Currency</span>
    </Link>
    {userRole === 'Admin' && (
      <Link
        to="/clients"
        className={`sidebar-item no-underline ${getIsActive("/clients") ? "active" : ""}`}
        title="Manage clients"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/clients'); }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <rect x="14" y="7" width="8" height="10" rx="2" ry="2" />
        </svg>
        <span>Client</span>
      </Link>
    )}
  </>
);

// Helper: Render Dashboard Links
const renderDashboardLinks = (getIsActive, setActiveSection) => (
  <Link
    to="/admin-dashboard"
    className={`sidebar-item no-underline ${getIsActive('/admin-dashboard') ? 'active' : ''}`}
    onClick={() => setActiveSection('adminDashboard')}
    title="View administrative dashboard with analytics"
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
    <span>Admin Dashboard</span>
  </Link>
);

const Sidebar = ({ setActiveSection, activeSection }) => {
  const [openSections, setOpenSections] = useState(() => {
    const saved = localStorage.getItem('sidebarOpenSections');
    return saved ? JSON.parse(saved) : { documents: false, favourite: false, master: false, dashboard: false };
  });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState("");
  const [userRole, setUserRole] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const profileRef = useRef(null);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const initials = userName ? userName.split(' ').map(name => name[0].toUpperCase()).join('') : 'U';

  useEffect(() => {
    const storedName = sessionStorage.getItem('user_name') || 'Unknown User';
    const storedEmail = sessionStorage.getItem("user_email") || sessionStorage.getItem("user_emailId") || "unknown@email.com";
    const storedRole = (sessionStorage.getItem("user_role") || "").replace(/\s+/g, '');
    setUserName(storedName);
    setUserEmail(storedEmail);
    setUserRole(storedRole);
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebarOpenSections', JSON.stringify(openSections));
  }, [openSections]);

  // Simplified toggleSection
  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleCollapse = () => setIsCollapsed((prev) => !prev);

  const getIsActive = (pathOrSection) =>
    pathOrSection.startsWith('/') ? location.pathname === pathOrSection : activeSection === pathOrSection;

  const handleLogout = async () => {
    try {
      setShowLogoutConfirm(false);
      setIsLoggingOut(true);

      localStorage.clear();
      sessionStorage.clear();

      setIsLoggingOut(false);

      const BASE_URL = window._env_?.REACT_APP_API_BASE_URL ||
        process.env.REACT_APP_API_BASE_URL ||
        "https://crclm-backend-dev.bravewave-24971d4f.eastus.azurecontainerapps.io/api";
      window.location.href = `${BASE_URL}/logout`;
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.clear();
      sessionStorage.clear();
      setIsLoggingOut(false);
      window.location.href = '/login';
    }
  };

  const handleConfirmLogout = (confirm) => {
    if (confirm) handleLogout();
    setShowLogoutConfirm(false);
  };

  const permissions = rolePermissions[userRole] || { sidebar: [], actions: false };
  const showMaster = permissions.sidebar.includes("Master") && userEmail.toLowerCase().includes("criticalriver");
  const isClientPartner = userRole === 'ClientPartner' || userRole === 'ClientPartners';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        // Close popups if needed
      }
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        // No action
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`} ref={sidebarRef}>
      {isLoggingOut && (
        <div className="logout-loader-overlay">
          <div className="logout-loader-content">
            <CircularProgress size={40} style={{ color: '#ff6b35' }} />
            <span>Logging out from your account...</span>
          </div>
        </div>
      )}

      <div className="logo-section">
        <img src={clmLogo} alt="Contract Lifecycle Management" className="logo" />
      </div>

      <div className="sidebar-main-content">
        {isCollapsed ? (
          <>
            <div className="sidebar-section" title="Documents">
              <Link to="/clm_home" className="sidebar-section-header no-underline" onClick={() => setActiveSection('documents')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                  <polyline points="13 2 13 9 20 9" />
                </svg>
              </Link>
            </div>
            <div className="sidebar-section" title="My Collections">
              <Link to="/favourites" className="sidebar-section-header no-underline">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </Link>
            </div>
            {showMaster && (
              <div className="sidebar-section" title="Master">
                <Link to="/roles" className="sidebar-section-header no-underline">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </Link>
              </div>
            )}
            {permissions.sidebar.includes("AdminDashboard") && (
              <div className="sidebar-section" title="Dashboard">
                <Link to="/admin-dashboard" className="sidebar-section-header no-underline" onClick={() => setActiveSection('adminDashboard')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                </Link>
              </div>
            )}
          </>
        ) : (
          <>
            <SidebarSection
              title="Documents"
              sectionKey="documents"
              isOpen={openSections.documents}
              toggleSection={toggleSection}
              titleTooltip="View all contracts, WIP contracts, and templates"
            >
              {renderDocumentsLinks(getIsActive, setActiveSection, navigate, isClientPartner, permissions)}
            </SidebarSection>

            <SidebarSection
              title="My Collections"
              sectionKey="favourite"
              isOpen={openSections.favourite}
              toggleSection={toggleSection}
              titleTooltip="Access your favorite contracts, archive, and recent activity"
            >
              {renderCollectionsLinks(getIsActive, permissions)}
            </SidebarSection>

            {showMaster && (
              <SidebarSection
                title="Master"
                sectionKey="master"
                isOpen={openSections.master}
                toggleSection={toggleSection}
                titleTooltip="Manage roles, contract statuses, and contract types"
              >
                {renderMasterLinks(getIsActive, userRole, navigate)}
              </SidebarSection>
            )}

            {permissions.sidebar.includes("AdminDashboard") && (
              <SidebarSection
                title="Dashboard"
                sectionKey="dashboard"
                isOpen={openSections.dashboard}
                toggleSection={toggleSection}
                titleTooltip="View analytics, reports, and administrative insights"
              >
                {renderDashboardLinks(getIsActive, setActiveSection)}
              </SidebarSection>
            )}
          </>
        )}
      </div>

      <div className="profile-section-1" style={{ background: '#ff6b35', borderRadius: '10px', padding: '10px' }}>
        <div className="profile-content-1" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            className="profile-avatar-1"
            ref={profileRef}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              backgroundImage: sessionStorage.getItem('profile_url') ? `url(${sessionStorage.getItem('profile_url')})` : 'none',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ff6b35',
              fontWeight: 'bold',
              fontSize: '14px',
              overflow: 'hidden',
            }}
          >
            {!sessionStorage.getItem('profile_url') && initials}
          </div>
          {!isCollapsed && (
            <div className="profile-info-1">
              <div className="profile-name-1" style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
                {userName}
              </div>
              <div className="profile-email-1" style={{ color: '#fff', fontSize: '12px' }}>
                {userRole}
              </div>
            </div>
          )}
        </div>

        {!isCollapsed && (
          <div className="button-container" style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '5px', marginLeft: '10px' }}>
            {userRole === 'Admin' && (
              <button
                onClick={() => navigate('/roles?view=user-details')}
                style={{
                  width: '70px',
                  height: '50px',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  color: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
                title="Settings"
              >
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <rect x="0" y="0" width="20" height="5" rx="2.5" fill="#fff" opacity="0.4" />
                  <circle cx="10" cy="2.5" r="3" fill="#fff" />
                  <rect x="0" y="8" width="20" height="5" rx="2.5" fill="#fff" opacity="0.4" />
                  <circle cx="2" cy="11.5" r="3" fill="#fff" />
                </svg>
                <span style={{ fontSize: '9px', marginTop: '5px' }}>Settings</span>
              </button>
            )}
            <button
              onClick={() => setIsModalOpen(true)}
              style={{
                width: '70px',
                height: '50px',
                borderRadius: '8px',
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              title="Profile"
            >
              <svg width="20" height="20" viewBox="0 0 20 20">
                <circle cx="10" cy="5" r="5" fill="none" stroke="#fff" strokeWidth="2" />
                <path d="M 2,13 Q 2,7 5,7 L 15,7 Q 18,7 18,13" fill="none" stroke="#fff" strokeWidth="2" />
              </svg>
              <span style={{ fontSize: '9px', marginTop: '5px' }}>Profile</span>
            </button>
            <button
              onClick={() => setShowLogoutConfirm(true)}
              style={{
                width: '70px',
                height: '50px',
                borderRadius: '8px',
                background: '#fff',
                border: 'none',
                color: '#ff6b35',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontWeight: '600'
              }}
              title="Logout"
            >
              <svg width="30" height="20" viewBox="0 0 20 20">
                <rect x="2" y="0" width="4" height="20" rx="1" fill="none" stroke="#ff6b35" strokeWidth="2" />
                <line x1="4" y1="10" x2="12" y2="10" stroke="#ff6b35" strokeWidth="2" />
                <polyline points="8,6 12,10 8,14" fill="none" stroke="#ff6b35" strokeWidth="2" strokeLinejoin="round" />
              </svg>
              <span style={{ fontSize: '9px', marginTop: '5px' }}>Logout</span>
            </button>
          </div>
        )}
      </div>

      {/* Logout Confirmation Modal */}
      {ReactDOM.createPortal(
        showLogoutConfirm && (
          <div className="template2 logout-confirm-overlay" onClick={() => setShowLogoutConfirm(false)}>
            <div className="template2 modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="image-section">
                <div className="logout-icon">
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                </div>
              </div>
              <div className="content-section">
                <h2>Confirm Logout</h2>
                <p className="quote">
                  "Managing contracts is not just about paperwork—it's about building trust and delivering value."
                </p>
                <div className="button-group">
                  <button className="btn-no" onClick={() => handleConfirmLogout(false)}>
                    Stay Logged In
                  </button>
                  <button className="btn-yes" onClick={() => handleConfirmLogout(true)}>
                    Yes, Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        ),
        document.body
      )}

      {/* Profile Modal */}
      {ReactDOM.createPortal(
        <ProfileModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          setIsLogoutPopupOpen={() => { }}
          setIsLoggingOut={setIsLoggingOut}
          navigate={navigate}
        />,
        document.body
      )}

      <button className="toggle-btn" onClick={toggleCollapse}>
        ≡
      </button>
    </div>
  );
};
Sidebar.propTypes = {
  setActiveSection: PropTypes.func.isRequired,
  activeSection: PropTypes.string.isRequired,
};
export default Sidebar;